/* eslint-disable react-native/no-inline-styles */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  Modal,
  KeyboardAvoidingView,
  TouchableOpacity,
} from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import GridSelectionModalStyles from './GridSelection.styles';
import { useColors } from '../../../hooks/useColors';
import theme from '../../../common/theme';
import {
  DOCKET_HEIGHT,
  DOCKET_WIDTH,
  GRID_OPTIONS,
} from '../../../common/constants';
import { useScale } from '../../../hooks/useScale';
import { LayoutStrategy } from '@oolio-group/domain';
import { getDefaultRowsPerPage } from '../../../utils/layoutHelper';
import { getDocketDisplayValue } from '../../../common/constants';

export interface GridSelectionModalProps {
  visible: boolean;
  onClose?: () => void | Promise<void>;
  onSave: (columns: number, rows: number) => void;
  currentColumns: number;
  currentRows: number;
  selectedLayoutStrategy: LayoutStrategy;
}

const GridSelectionModal: React.FC<GridSelectionModalProps> = ({
  visible,
  onClose = () => undefined,
  onSave,
  currentColumns = 8,
  currentRows = 2,
  selectedLayoutStrategy,
}) => {
  const { translate } = useTranslation();
  const styles = GridSelectionModalStyles();
  const { colors } = useColors();
  const [columns, setColumns] = useState<number>(currentColumns);
  const [rows, setRows] = useState<number>(currentRows);
  const { scale } = useScale();

  const onPressSave = useCallback(() => {
    onSave(columns, rows);
  }, [onSave, columns, rows]);

  const { vw, vh } = theme.window;

  const maxSupportedColumns = useMemo(() => {
    return Math.floor(vw / scale(DOCKET_WIDTH));
  }, [vw, scale]);

  const maxSupportedRows = useMemo(() => {
    return Math.floor(vh / scale(DOCKET_HEIGHT));
  }, [vh, scale]);

  const onColumnsChange = useCallback((value: number) => {
    setColumns(value);
  }, []);

  const onRowsChange = useCallback((value: number) => {
    setRows(value);
  }, []);

  const isRowOptionDisabled = useCallback(
    (option: number) => {
      return (
        option > maxSupportedRows ||
        (selectedLayoutStrategy !== LayoutStrategy.GRID &&
          option !== getDefaultRowsPerPage(selectedLayoutStrategy))
      );
    },
    [maxSupportedRows, selectedLayoutStrategy],
  );

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <KeyboardAvoidingView style={styles.background}>
        <View style={styles.container}>
          <Text style={styles.title}>
            {translate('kitchenDisplay.setGridSize')}
          </Text>
          <View>
            <Text style={styles.sectionLabel}>
              {translate('common.columns')}
            </Text>
            <View style={styles.gridContainer}>
              {GRID_OPTIONS.map(option => (
                <TouchableOpacity
                  key={`column-${option}`}
                  style={[
                    styles.gridOption,
                    {
                      backgroundColor:
                        columns === option ? colors.accent : colors.background,
                      opacity: option > maxSupportedColumns ? 0.5 : 1,
                    },
                  ]}
                  disabled={option > maxSupportedColumns}
                  onPress={() => onColumnsChange(option)}
                >
                  <Text
                    style={[
                      styles.gridOptionText,
                      {
                        color: columns === option ? colors.white : colors.text,
                      },
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={styles.noteText}>
              {translate('kitchenDisplay.columnPerScreenNotes', {
                maxSupportedColumns,
              })}
            </Text>
            <Text style={styles.sectionLabel}>{translate('common.rows')}</Text>
            <View style={styles.gridContainer}>
              {GRID_OPTIONS.map(option => (
                <TouchableOpacity
                  key={`row-${option}`}
                  style={[
                    styles.gridOption,
                    {
                      backgroundColor:
                        rows === option ? colors.accent : colors.background,
                      opacity: isRowOptionDisabled(option) ? 0.5 : 1,
                    },
                  ]}
                  disabled={isRowOptionDisabled(option)}
                  onPress={() => onRowsChange(option)}
                >
                  <Text
                    style={[
                      styles.gridOptionText,
                      {
                        color: rows === option ? colors.white : colors.text,
                      },
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={styles.noteText}>
              {selectedLayoutStrategy === LayoutStrategy.GRID
                ? translate('kitchenDisplay.rowPerScreenNotes', {
                    maxSupportedRows,
                  })
                : translate('kitchenDisplay.defaultRowsNote', {
                    rows: getDefaultRowsPerPage(selectedLayoutStrategy),
                    layout: getDocketDisplayValue(selectedLayoutStrategy),
                  })}
            </Text>

            <TouchableOpacity
              style={[
                styles.confirmBtn,
                {
                  backgroundColor:
                    columns <= 0 || rows <= 0
                      ? colors.background
                      : colors.accent,
                },
              ]}
              onPress={onPressSave}
              disabled={columns <= 0 || rows <= 0}
            >
              <Text style={[styles.confirmBtnText, { color: colors.white }]}>
                {translate('button.confirm')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.dismissBtn} onPress={onClose}>
              <Text style={[styles.dismissBtnText, { color: colors.text }]}>
                {translate('button.dismiss')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default GridSelectionModal;
